import React, { useContext, createContext, useState, useCallback } from "react";
import {
  useGlobalContext,
  GlobalContextObject,
  globalContextDefaults,
} from "../../../../globalContext";
import { FetchAPIOptions } from "../../../../types/fetch";

export type User = {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  client_id: string;
  country_id: string;
  release_user: boolean;
  release_password: boolean;
  error: null | string;
};

export const responseInitials = {
  already_exists: [] as User[],
  rejected_data: [] as User[],
  columns: [] as string[],
};

export type keyType = "already_exists" | "rejected_data";
export type UniqueUser = User & { _uniqueId: string };

type ResponseType = typeof responseInitials;

type UploadUserContextType = {
  fetchAPI: GlobalContextObject["fetchAPI"];
  showModalSuccess: GlobalContextObject["showModalSuccess"];
  addUserList: (userList: any, key: string) => Promise<void>;
  updateList: (userList: any, key: string) => void;
  uploadUsers: (file: File) => Promise<void>;
  resetData: () => void;
  router: GlobalContextObject["router"];
  response: ResponseType;
  loading: boolean;
  jsonDataLoading: boolean;
};

const UploadUserContextDefaults: UploadUserContextType = {
  fetchAPI: globalContextDefaults.fetchAPI,
  showModalSuccess: globalContextDefaults.showModalSuccess,
  addUserList: async () => {},
  updateList: () => {},
  uploadUsers: async () => {},
  resetData: () => {},
  router: globalContextDefaults.router,
  response: responseInitials,
  loading: false,
  jsonDataLoading: false,
};

const UploadUserContext = createContext<UploadUserContextType>(UploadUserContextDefaults);

type UploadUserProviderProps = {
  children: React.ReactNode;
};

export const UploadUserProvider = ({ children }: UploadUserProviderProps) => {
  const { fetchAPI, showModalSuccess, showModalError, router } = useGlobalContext();

  const [response, setResponse] = useState<ResponseType>(responseInitials);
  const [loading, setLoading] = useState<boolean>(false);
  const [jsonDataLoading, setJsonDataLoading] = useState<boolean>(false);

  const handleResponse = useCallback(
    (response) => {
      const { created_users, rejected_data, already_exists, length, error } = response;

      if (error) {
        showModalError(error);
      } else if (length === created_users?.length) {
        showModalSuccess("All users uploaded successfully.");
      } else if (length === rejected_data?.length) {
        showModalError("All users rejected.");
      } else if (length === already_exists?.length) {
        showModalError("All users already exist.");
      } else {
        let message = "";
        if (created_users?.length) {
          message += `${created_users.length} users uploaded successfully. \n`;
        }
        if (rejected_data?.length) {
          message += `${rejected_data.length} users rejected. \n`;
        }
        if (already_exists?.length) {
          message += `${already_exists.length} users already exist.`;
        }
        showModalSuccess(message);
      }
    },
    [showModalSuccess, showModalError]
  );

  const uploadUsers = useCallback(
    async (file: File) => {
      try {
        setLoading(true);

        const formData = new FormData();
        formData.append("file", file);

        const response = await fetchAPI(
          "users/upload/",
          formData as unknown as FetchAPIOptions
        );

        if (response?.data?.success === "OK") {
          handleResponse(response.data);
          setResponse(response.data);
        } else {
          throw new Error();
        }
      } catch (error) {
        showModalError("Failed to upload users. Please try again.");
      }
      setLoading(false);
    },
    [fetchAPI, showModalError, handleResponse]
  );

  const addUserList = useCallback(
    async (userList, key) => {
      setJsonDataLoading(true);
      try {
        const resposne = await fetchAPI("users/json/", userList);
        const otherKey = key === "rejected_data" ? "already_exists" : "rejected_data";
        if (resposne?.data?.success === "OK") {
          handleResponse(resposne?.data);

          if (!resposne?.data?.error)
            setResponse((prevResponse) => ({
              ...prevResponse,
              [key]: resposne?.data?.[key],
              [otherKey]: [
                ...(prevResponse?.[otherKey] || []),
                ...(resposne?.data?.[otherKey] || []),
              ],
            }));
        } else {
          throw new Error();
        }
      } catch (error) {
        showModalError("Failed to add users. Please try again.");
      }
      setJsonDataLoading(false);
    },
    [fetchAPI, setJsonDataLoading, showModalError, setResponse, handleResponse]
  );

  const updateList = useCallback(
    (userList, key) => {
      setResponse((prevResponse) => ({
        ...prevResponse,
        [key]: userList,
      }));
    },
    [setResponse]
  );

  const resetData = useCallback(() => {
    setResponse(responseInitials);
  }, []);

  const value = {
    fetchAPI,
    showModalSuccess,
    response,
    loading,
    jsonDataLoading,
    uploadUsers,
    addUserList,
    updateList,
    router,
    resetData,
  };

  return (
    <UploadUserContext.Provider value={value}>{children}</UploadUserContext.Provider>
  );
};

export const useUploadUserContext = () => {
  const context = useContext(UploadUserContext);
  return context;
};
